/**
 * Which sections of a document's detail rail are folded: each folds on its own heading and opens
 * again from the row of shortcuts above them, which also scrolls to it. The choice is the
 * reader's own convenience, kept in this browser (a private window starts from the default).
 */
import { useCallback, useState } from 'react'

export type RailSectionId =
  | 'summary'
  | 'transcript'
  | 'events'
  | 'people'
  | 'faces'
  | 'plates'
  | 'entities'
  | 'relationships'
  | 'chunks'

const STORAGE_KEY = 'xendrel.documentRail.folded'
/** Folded until opened: the indexed chunks are long and read least. */
const DEFAULT_FOLDED: readonly RailSectionId[] = ['chunks']

/** The anchor a section's shortcut scrolls to. */
export const railSectionAnchor = (id: RailSectionId) => `document-rail-${id}`

function stored(): Set<RailSectionId> {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (raw) return new Set(JSON.parse(raw) as RailSectionId[])
  } catch {
    // No storage (a private window, blocked site data): the default holds.
  }
  return new Set(DEFAULT_FOLDED)
}

function keep(folded: Set<RailSectionId>) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...folded]))
  } catch {
    // Not kept: the rail still folds for this visit.
  }
}

export interface RailSections {
  isOpen: (id: RailSectionId) => boolean
  setOpen: (id: RailSectionId, open: boolean) => void
  /** Open a section and bring it into view. */
  jumpTo: (id: RailSectionId) => void
}

export function useRailSections(): RailSections {
  const [folded, setFolded] = useState<Set<RailSectionId>>(stored)
  const setOpen = useCallback((id: RailSectionId, open: boolean) => {
    setFolded(current => {
      const next = new Set(current)
      if (open) next.delete(id)
      else next.add(id)
      keep(next)
      return next
    })
  }, [])
  const jumpTo = useCallback(
    (id: RailSectionId) => {
      setOpen(id, true)
      window.requestAnimationFrame(() =>
        document
          .getElementById(railSectionAnchor(id))
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      )
    },
    [setOpen]
  )
  return { isOpen: id => !folded.has(id), setOpen, jumpTo }
}
